import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig } from "axios";
import { HttpClientConfig } from "./http-types";
import { HttpErrorHandler } from "./error/http-error-handler";
import { IBaseResponse } from "@/shared/services/interface/responses/IBaseResponse";
import authService from "@/shared/services/api/auth-service";
import { ROUTES } from "@/core/router/routes";
import { trackAPIError, trackSessionExpired } from "@/lib/rum-actions";
import { getEnv } from "@/shared/utils/env";

// Extend AxiosRequestConfig to include timing metadata
interface RequestConfigWithTiming extends InternalAxiosRequestConfig {
  metadata?: {
    startTime: number;
  };
}

class HttpClient {
  private readonly client: AxiosInstance;
  private refreshPromise: Promise<string> | null = null;

  constructor(config?: HttpClientConfig) {
    const language = localStorage.getItem("i18nextLng");
    const accessToken = localStorage.getItem("accessToken");

    let headers: Record<string, string | null> = {
      "Content-Type": "application/json",
      ...config?.headers,
      "x-custom-lang": language,
    };

    if (accessToken) {
      headers = {
        ...headers,
        Authorization: `Bearer ${accessToken}`,
      };
    }

    this.client = axios.create({
      // Important: this module exports a singleton instance (`httpClient`) which is
      // constructed during module evaluation (before `main.tsx` executes).
      // We cannot rely on Vite env being available yet, so baseURL is patched later
      // from `main.tsx` after `setViteEnv(...)` is called.
      baseURL: config?.baseURL ?? "",
      headers: headers,
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // İstek interceptor'ı
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem("accessToken");
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        // RUM-043: Add timing metadata for response time tracking
        (config as RequestConfigWithTiming).metadata = { startTime: Date.now() };
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Yanıt interceptor'ı
    this.client.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error) => {
        const originalRequest = error.config as RequestConfigWithTiming;

        // RUM-043: Track API error
        const startTime = originalRequest?.metadata?.startTime;
        const responseTimeMs = startTime ? Date.now() - startTime : 0;
        trackAPIError(
          originalRequest?.url || "unknown",
          originalRequest?.method?.toUpperCase() || "UNKNOWN",
          error.response?.status || 0,
          error.response?.data?.message || error.message || "Unknown error",
          responseTimeMs
        );
        // handle user not found
        // if (
        //   originalRequest.url?.includes("/auth/otp/send") &&
        //   error.response?.status === 404
        // ) {
        //   return HttpErrorHandler.handle(error, {
        //     title:
        //       "Account not found. <span class='text-muted-foreground font-500'>Signup instead?</span>",
        //     position: "top-center",
        //     progress: false,
        //   });
        // }

        // Refresh token isteğinde hata alındıysa direkt logout yapılmalı
        if (originalRequest.url?.includes("/refresh-token")) {
          // RUM-040: Track session expired when refresh token fails
          trackSessionExpired("refresh_token_failed", 0, 0);
          await this.logout();
          return HttpErrorHandler.handle(error);
        }

        // 401 hatası ve henüz retry yapılmamışsa
        if (error.response?.status === 401 && !(originalRequest as RequestConfigWithTiming & { _retry?: boolean })._retry) {
          (originalRequest as RequestConfigWithTiming & { _retry?: boolean })._retry = true;

          try {
            const refreshToken = localStorage.getItem("refreshToken");
            if (refreshToken === "norefresh") {
              // Anonymous/share sessions do not have refresh flow.
              return HttpErrorHandler.handle(error);
            }

            if (!refreshToken) {
              // RUM-040: Track session expired when no refresh token available
              trackSessionExpired("no_refresh_token", 0, 0);
              await this.logout();
              return HttpErrorHandler.handle(error);
            }

            const accessToken = await this.refreshAccessToken(refreshToken);
            localStorage.setItem("accessToken", accessToken);
            this.setAuthToken(accessToken);
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;

            // Yeni token ile orijinal isteği tekrar gönder
            return this.client(originalRequest);
          } catch (refreshError) {
            // RUM-040: Track session expired when refresh token API fails
            trackSessionExpired("refresh_api_error", 0, 0);
            await this.logout();
            return HttpErrorHandler.handle(refreshError as AxiosError);
          }
        }

        return HttpErrorHandler.handle(error);
      }
    );
  }

  async logout(): Promise<void> {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("user");
    localStorage.clear();
    setTimeout(() => {
      window.location.href = `/${ROUTES.Auth.Root}/${ROUTES.Auth.SignIn}`;
    }, 1000);
  }

  private async refreshAccessToken(refreshToken: string): Promise<string> {
    if (!this.refreshPromise) {
      this.refreshPromise = authService
        .refreshToken({ refreshToken })
        .then((response) => {
          const accessToken = response.data?.accessToken;
          if (!accessToken) {
            throw new Error("Refresh token response does not include access token");
          }
          return accessToken;
        })
        .finally(() => {
          this.refreshPromise = null;
        });
    }

    return this.refreshPromise;
  }

  private async request<T>(
    config: AxiosRequestConfig
  ): Promise<IBaseResponse<T>> {
    const response = await this.client.request<IBaseResponse<T>>(config);
    return response.data;
  }

  public async get<T>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<IBaseResponse<T>> {
    return this.request<T>({ ...config, method: "GET", url });
  }

  public async post<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<IBaseResponse<T>> {
    return this.request<T>({ ...config, method: "POST", url, data });
  }

  public async postMultipart<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig & {
      onUploadProgress?: (progressEvent: any) => void;
    }
  ): Promise<IBaseResponse<T>> {
    return this.request<T>({
      ...config,
      headers: {
        ...config?.headers,
        "Content-Type": "multipart/form-data",
      },
      method: "POST",
      url,
      data,
      onUploadProgress: config?.onUploadProgress,
    });
  }

  public async put<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<IBaseResponse<T>> {
    return this.request<T>({ ...config, method: "PUT", url, data });
  }

  public async patch<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<IBaseResponse<T>> {
    return this.request<T>({ ...config, method: "PATCH", url, data });
  }

  public async delete<T>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<IBaseResponse<T>> {
    return this.request<T>({ ...config, method: "DELETE", url });
  }

  public setAuthToken(token: string): void {
    this.client.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  }

  public setBaseURL(baseURL: string): void {
    this.client.defaults.baseURL = baseURL;
  }

  public removeAuthToken(): void {
    delete this.client.defaults.headers.common["Authorization"];
  }
}

export const httpClient = new HttpClient();
export default HttpClient;
